import { useState } from 'react';
import { useToast } from '@/hooks/use-toast';
import { callEdge } from '@/lib/edgeClient';

interface AnalysisResult {
  analysis: string;
  tokens_used: number;
  response_time_ms: number;
  analysis_type: string;
  frames_analyzed: number;
  pose_frames_analyzed?: number;
  error?: string;
}

interface ComparisonResults {
  pose: AnalysisResult | null;
  video: AnalysisResult | null;
  combined: AnalysisResult | null;
}

export function useAnalysisComparison() {
  const [isAnalyzing, setIsAnalyzing] = useState({
    pose: false,
    video: false,
    combined: false
  });
  const [results, setResults] = useState<ComparisonResults>({
    pose: null,
    video: null,
    combined: null
  });
  const { toast } = useToast();

  const clearResults = () => {
    setResults({ pose: null, video: null, combined: null });
  };

  const runPoseAnalysis = async (poseData: any[]) => {
    setIsAnalyzing(prev => ({ ...prev, pose: true }));
    
    try {
      const response = await callEdge('pose-analyze', {
        method: 'POST',
        body: { frames_sample: poseData },
        authRequired: true
      });

      if (response.status !== 200) {
        throw new Error(response.body?.error || 'Failed to analyze pose data');
      }

      setResults(prev => ({ ...prev, pose: response.body }));
      return response.body;
    } catch (error) {
      console.error('Pose analysis error:', error);
      setResults(prev => ({ 
        ...prev, 
        pose: { error: error.message, analysis: '', tokens_used: 0, response_time_ms: 0, analysis_type: 'pose_only', frames_analyzed: 0 }
      }));
      return null;
    } finally {
      setIsAnalyzing(prev => ({ ...prev, pose: false }));
    }
  };

  const runVideoAnalysis = async (videoFrames: any[]) => {
    setIsAnalyzing(prev => ({ ...prev, video: true }));
    
    try {
      const response = await callEdge('pose-analyze-video-only', {
        method: 'POST',
        body: { video_frames: videoFrames },
        authRequired: true
      });

      if (response.status !== 200) {
        throw new Error(response.body?.error || 'Failed to analyze video frames');
      }

      setResults(prev => ({ ...prev, video: response.body }));
      return response.body;
    } catch (error) {
      console.error('Video analysis error:', error);
      setResults(prev => ({ 
        ...prev, 
        video: { error: error.message, analysis: '', tokens_used: 0, response_time_ms: 0, analysis_type: 'video_only', frames_analyzed: 0 }
      }));
      return null;
    } finally {
      setIsAnalyzing(prev => ({ ...prev, video: false }));
    }
  };

  const runCombinedAnalysis = async (videoFrames: any[], poseData: any[]) => {
    setIsAnalyzing(prev => ({ ...prev, combined: true }));
    
    try {
      const response = await callEdge('pose-analyze-combined', {
        method: 'POST',
        body: { 
          video_frames: videoFrames,
          pose_data: poseData
        },
        authRequired: true
      });

      if (response.status !== 200) {
        throw new Error(response.body?.error || 'Failed to analyze combined data');
      }

      setResults(prev => ({ ...prev, combined: response.body }));
      return response.body;
    } catch (error) {
      console.error('Combined analysis error:', error);
      setResults(prev => ({ 
        ...prev, 
        combined: { error: error.message, analysis: '', tokens_used: 0, response_time_ms: 0, analysis_type: 'video_and_pose', frames_analyzed: 0 }
      }));
      return null;
    } finally {
      setIsAnalyzing(prev => ({ ...prev, combined: false }));
    }
  };

  const runAllAnalyses = async (poseData: any[], playbackId: string) => {
    // Generate video frame URLs for consistent comparison
    const videoFrames = [
      { url: `https://yhcymqfdigeufzooutxi.functions.supabase.co/signed-thumb?playbackId=${playbackId}&time=1&width=720` },
      { url: `https://yhcymqfdigeufzooutxi.functions.supabase.co/signed-thumb?playbackId=${playbackId}&time=3&width=720` },
      { url: `https://yhcymqfdigeufzooutxi.functions.supabase.co/signed-thumb?playbackId=${playbackId}&time=5&width=720` }
    ];

    // Run all three analyses simultaneously
    const promises = [
      runPoseAnalysis(poseData),
      runVideoAnalysis(videoFrames),
      runCombinedAnalysis(videoFrames, poseData)
    ];

    try {
      const [poseResult, videoResult, combinedResult] = await Promise.allSettled(promises);
      
      toast({
        title: "Analysis Complete",
        description: "All three analysis methods completed successfully"
      });

      return {
        pose: poseResult.status === 'fulfilled' ? poseResult.value : null,
        video: videoResult.status === 'fulfilled' ? videoResult.value : null,
        combined: combinedResult.status === 'fulfilled' ? combinedResult.value : null
      };
    } catch (error) {
      toast({
        title: "Analysis Error",
        description: "Some analyses failed to complete",
        variant: "destructive"
      });
      return null;
    }
  };

  const exportResults = () => {
    const exportData = {
      timestamp: new Date().toISOString(),
      results,
      summary: {
        total_analyses: 3,
        total_tokens: (results.pose?.tokens_used || 0) + (results.video?.tokens_used || 0) + (results.combined?.tokens_used || 0),
        total_time_ms: (results.pose?.response_time_ms || 0) + (results.video?.response_time_ms || 0) + (results.combined?.response_time_ms || 0)
      }
    };

    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `analysis-comparison-${Date.now()}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    toast({
      title: "Export Complete",
      description: "Analysis comparison data downloaded successfully"
    });
  };

  return {
    runPoseAnalysis,
    runVideoAnalysis,
    runCombinedAnalysis,
    runAllAnalyses,
    exportResults,
    clearResults,
    isAnalyzing,
    results
  };
}